import React from 'react';
import keyBy from 'lodash/keyBy';

import { Field, FieldType, useFormFieldValue } from '@/components/Form';
import { DatabaseFieldEditorConfig } from '@/modules/corporate/modules/database/fields/types';

import * as FieldUtils from '../../utils/editor/config';
import { fieldSettingsSectionStyles } from '../../utils/editor/styles';
import RatingLabelsFormField from '../components/RatingLabelsFormField';
import RatingLevelFormField from '../components/RatingLevelFormField';
import RatingStyleFormField from '../components/RatingStyleFormField';
import { adaptFormField } from '../utils';

const RatingFieldEditorConfig: DatabaseFieldEditorConfig = {
  getFieldSettingsFormProps: ({ t, allowPartialUpdates }) => {
    const fields: Field[] = [
      FieldUtils.getDatabaseTypeFormField(t, allowPartialUpdates),
      {
        name: 'metadataField.ratingType',
        type: FieldType.Custom,
        label: t('Database:Style'),
        Component: ({ value, onChange }) => {
          return <RatingStyleFormField value={value} onChange={onChange} />;
        },
      },
      {
        name: 'metadataField.validation.maxValue',
        label: t('Database:RatingLevels.Levels'),
        type: FieldType.SlowCustom,
        Component: ({ value, onChange }) => {
          return (
            <RatingLevelFormField
              disabled={allowPartialUpdates}
              value={value}
              onChange={onChange}
            />
          );
        },
      },
      {
        name: 'viewField.lables',
        type: FieldType.Custom,
        label: t('Database:RatingLevels.Labels'),
        Component: ({ setFieldValue }) => {
          const fieldKey = 'viewField';
          const value = useFormFieldValue(fieldKey);
          const maxValue = useFormFieldValue('metadataField.validation.maxValue');

          const onChange = (nextValue: any) => {
            setFieldValue(fieldKey, { ...value, ...nextValue }, true);
          };

          return <RatingLabelsFormField maxValue={maxValue} value={value} onChange={onChange} />;
        },
      },
      FieldUtils.getRequiredFormField(t),
      FieldUtils.getDescriptionEnabledFormField(t),
    ];

    return {
      fields: keyBy(fields, 'name'),
      sectionLayout: [
        {
          id: 'ratings-settings',
          className: fieldSettingsSectionStyles(32),
          layout: ['metadataField.type', 'metadataField.ratingType'],
        },
        {
          id: 'ratings-labels-settings',
          className: fieldSettingsSectionStyles(32),
          layout: ['metadataField.validation.maxValue', 'viewField.lables'],
        },
        {
          id: 'rating-field-view-settings',
          className: fieldSettingsSectionStyles(24),
          layout: ['viewField.required', 'viewField.descriptionEnabled'],
        },
      ],
      overrides: FieldUtils.FormOverrides,
    };
  },
  getFieldSettingsFormInitialValues: ({ metadataField, viewField }) => ({
    metadataField: {
      type: metadataField.type,
      validation: metadataField.validation,
      ratingType: metadataField.ratingType,
    },
    viewField: {
      required: viewField.required,
      descriptionEnabled: viewField.descriptionEnabled,
      minLabel: viewField.minLabel,
      maxLabel: viewField.maxLabel,
    },
  }),
  adaptEditorFormField: adaptFormField,
};

export default RatingFieldEditorConfig;
